import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import BaseWrapper from "../../components/ui/BaseWrapper";
import TaskCard from "./components/TaskCard";
import { TeamTaskStatusType } from "../../utils/enum";
import { AvatarGroup, Skeleton } from "@mui/material";
import BaseTextField from "../../components/ui/BaseTextField";
import { debounce } from "lodash";
import TaskBoardFilters from "./components/TaskBoardFilters";
import { useDispatch, useSelector } from "react-redux";
import { setTaskBoardFilter } from "../../store/filters/taskBoardFiltersSlice";
import { fetchTeamMembersByTeamIds, fetchTeams } from "../../services/api/teams";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchAllTeamTask, updateTaskStatus } from "../../services/api/teamTask";
import { fetchCompanyUsers } from "../../services/api/company";
import { useNavigate, useParams } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Oval } from "react-loader-spinner";
import { brandSecondaryColor } from "../../utils/variables";

const PAGE_SIZE = 4;

const COLUMNS = [
  { status: TeamTaskStatusType.TODO, label: "To do", hasBorder: true },
  { status: TeamTaskStatusType.INPROCESS, label: "In-Progress", hasBorder: true },
  { status: TeamTaskStatusType.ON_HOLD, label: "On Hold", hasBorder: true },
  { status: TeamTaskStatusType.COMPLETED, label: "Completed", hasBorder: false },
];

const TaskBoardListing = ({
  isAddEditTaskModal,
  setIsAddEditTaskModal,
  tasksList,
  refetchAllTeamTask,
  setSelectedTask,
  taskBoardFilters,
  isCompanyOwner,
  queryParams,
}) => {
  const { id: taskId } = useParams();
  const navigate = useNavigate();
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const user = useSelector((state) => state.authUser.value.user);
  const selectedTeamIds = user?.teams?.map((team) => team.id);

  // The task id in the URL is what drives the modal, so deep links open it and the
  // browser's back button closes it again instead of stranding it over the board.
  useEffect(() => {
    if (parseInt(taskId)) {
      setIsAddEditTaskModal(true);
    } else {
      setIsAddEditTaskModal(false);
    }
  }, [taskId]);

  // Per-status infinite scroll queries
  const makeQueryOptions = (status) => ({
    queryKey: ["tasks", status, queryParams],
    queryFn: ({ pageParam }) =>
      fetchAllTeamTask({ ...queryParams, status, page: pageParam, pageSize: PAGE_SIZE }),
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, p) => sum + (p.data?.data?.length || 0), 0);
      const total = lastPage.data?.totalCount || 0;
      return loaded < total ? allPages.length + 1 : undefined;
    },
    initialPageParam: 1,
  });

  const todoQuery = useInfiniteQuery(makeQueryOptions(TeamTaskStatusType.TODO));
  const inprocessQuery = useInfiniteQuery(makeQueryOptions(TeamTaskStatusType.INPROCESS));
  const onholdQuery = useInfiniteQuery(makeQueryOptions(TeamTaskStatusType.ON_HOLD));
  const completedQuery = useInfiniteQuery(makeQueryOptions(TeamTaskStatusType.COMPLETED));

  const columnQueries = {
    [TeamTaskStatusType.TODO]: todoQuery,
    [TeamTaskStatusType.INPROCESS]: inprocessQuery,
    [TeamTaskStatusType.ON_HOLD]: onholdQuery,
    [TeamTaskStatusType.COMPLETED]: completedQuery,
  };

  // Filter sidebar queries (only when drawer is open)
  const { isPending: isFetchTeamMembersLoading, data: teamMembersData } = useQuery({
    queryKey: ["teamMembers", selectedTeamIds],
    queryFn: () => fetchTeamMembersByTeamIds(selectedTeamIds),
    enabled: isFilterDrawerOpen,
  });
  const { data: allTeamTaskData } = useQuery({
    queryKey: ["teamTasks", selectedTeamIds],
    queryFn: () => fetchAllTeamTask({ teamIds: selectedTeamIds }),
    enabled: isFilterDrawerOpen,
  });
  const { data: companyUsers } = useQuery({
    queryKey: ["companyUsers"],
    queryFn: () => fetchCompanyUsers(),
    enabled: isFilterDrawerOpen,
  });
  const { data: teamData } = useQuery({
    queryKey: ["teamData"],
    queryFn: () => fetchTeams(),
    enabled: isCompanyOwner && isFilterDrawerOpen,
  });

  const { mutateAsync: updateStatusAsync } = useMutation({ mutationFn: updateTaskStatus });

  // Drag and drop handler
  const onDragEnd = useCallback(
    async ({ source, destination, draggableId }) => {
      if (!destination || source.droppableId === destination.droppableId) return;

      const sourceStatus = source.droppableId;
      const destStatus = destination.droppableId;
      const taskId = draggableId;

      const getKey = (status) => ["tasks", status, queryParams];

      // Find the dragged task in source column cache
      const sourceCache = queryClient.getQueryData(getKey(sourceStatus));
      let movedTask = null;
      sourceCache?.pages?.forEach((page) => {
        page.data?.data?.forEach((t) => {
          if (t.id === taskId) movedTask = { ...t, status: destStatus };
        });
      });
      if (!movedTask) return;

      // Snapshot for rollback
      const prevSource = queryClient.getQueryData(getKey(sourceStatus));
      const prevDest = queryClient.getQueryData(getKey(destStatus));

      // Optimistic: remove from source column
      queryClient.setQueryData(getKey(sourceStatus), (old) => ({
        ...old,
        pages: old.pages.map((page) => ({
          ...page,
          data: {
            ...page.data,
            data: page.data.data.filter((t) => t.id !== taskId),
            totalCount: Math.max(0, page.data.totalCount - 1),
          },
        })),
      }));

      // Optimistic: insert at top of destination column
      queryClient.setQueryData(getKey(destStatus), (old) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page, i) =>
            i === 0
              ? {
                  ...page,
                  data: {
                    ...page.data,
                    data: [movedTask, ...page.data.data],
                    totalCount: page.data.totalCount + 1,
                  },
                }
              : page,
          ),
        };
      });

      try {
        await updateStatusAsync({ taskId, status: destStatus });
      } catch {
        // Revert on failure
        queryClient.setQueryData(getKey(sourceStatus), prevSource);
        queryClient.setQueryData(getKey(destStatus), prevDest);
      }
    },
    [queryClient, queryParams, updateStatusAsync],
  );

  const appliedFilters = useMemo(() => {
    const wf = taskBoardFilters;
    const result = [];

    if (wf?.search) result.push({ prefix: "Search", value: wf.search, key: "search" });

    if (wf?.priority?.value) {
      const priorityLabel = typeof wf.priority === "object" ? wf.priority.label : wf.priority;
      result.push({ prefix: "Priority", value: priorityLabel, key: "priority" });
    }

    if (wf?.taskIds?.length) {
      const tasksData = allTeamTaskData?.data?.data || [];
      const items = tasksData
        .filter((t) => wf.taskIds.includes(typeof t === "object" ? (t.value ?? t.id) : t))
        .map((t) => ({ id: t.id, name: t.title }));
      items.forEach(({ id, name }) =>
        result.push({ prefix: "Task", value: name, key: "taskIds", id }),
      );
    }

    if (wf?.createdByIds?.length) {
      const membersData = companyUsers?.data?.users || [];
      const items = membersData
        .filter((m) => wf.createdByIds.includes(typeof m === "object" ? (m.value ?? m.id) : m))
        .map((m) => ({ id: m.value ?? m.id, name: m.label ?? `${m.firstName} ${m.lastName}` }));
      items.forEach(({ id, name }) =>
        result.push({ prefix: "Created By", value: name, key: "createdByIds", id }),
      );
    }

    if (wf?.assignees?.length) {
      const membersData = teamMembersData?.data || [];
      const items = membersData
        .filter((m) => wf.assignees.includes(typeof m === "object" ? (m.value ?? m.id) : m))
        .map((m) => ({ id: m.value ?? m.id, name: m.label ?? `${m.firstName} ${m.lastName}` }));
      items.forEach(({ id, name }) =>
        result.push({ prefix: "Assignee", value: name, key: "assignees", id }),
      );
    }

    if (wf?.teamIds?.length) {
      const teamsData = isCompanyOwner ? teamData?.data?.data : user?.teams || [];
      const items = teamsData
        .filter((t) => wf.teamIds.includes(t.id))
        .map((t) => ({ id: t.id, name: t.name }));
      items.forEach(({ id, name }) =>
        result.push({ prefix: "Team", value: name, key: "teamIds", id }),
      );
    }

    if (wf?.workOrder?.value) {
      const workOrderLabel = typeof wf.workOrder === "object" ? wf.workOrder.label : wf.workOrder;
      result.push({ prefix: "Work Order", value: workOrderLabel, key: "workOrder" });
    }

    return result;
  }, [taskBoardFilters, allTeamTaskData, teamMembersData?.data, companyUsers, teamData, user?.teams]);

  const handleRemoveFilter = (key, id) => {
    const current = taskBoardFilters[key];
    dispatch(
      setTaskBoardFilter({
        module: "tasks",
        filterName: key,
        data: Array.isArray(current) ? current.filter((x) => x !== id) : "",
      }),
    );
    refetchAllTeamTask();
  };

  const handleSearchChange = useCallback(
    debounce((val) => {
      dispatch(setTaskBoardFilter({ module: "tasks", filterName: "search", data: val }));
      refetchAllTeamTask();
    }, 500),
    [],
  );

  const handleTaskClick = (task) => {
    setIsAddEditTaskModal(true);
    setSelectedTask(task);
    // Absolute path, through the router. A relative raw pushState here used to
    // stack up as /taskBoard/taskBoard/<id> and left the router's location out of
    // sync, so the next real navigation back to this URL matched no route.
    navigate(`/taskBoard/${task.referenceId}`);
  };

  return (
    <BaseWrapper>
      {/* Filter bar */}
      <div className="bg-white rounded-lg px-5 py-2 shadow-md my-3 sticky z-10 flex justify-between items-center">
        <div className="flex items-center border-r-[1px] border-[#8C8C8C] pr-5">
          <span className="flex items-center justify-center w-7 h-7 rounded-full bg-primary"><i className="fa-light fa-filter text-white text-sm"></i></span>
          <p
            className="text-primary ml-2 cursor-pointer"
            onClick={() => setIsFilterDrawerOpen(true)}
          >
            Add Filter
          </p>
        </div>
        <div className="flex-1 min-w-0 pl-3 overflow-x-auto whitespace-nowrap py-1 scrollbar">
          <div className="flex gap-2">
            {appliedFilters.map((f) => (
              <div
                key={(f.key || "") + (f.value || "") + (f.id || "")}
                className="flex-shrink-0 flex items-center bg-[#EAF1FF] text-sm font-medium rounded-md px-3 py-1"
              >
                <span className="text-primary">{f.prefix}:</span>&nbsp;
                <p className="text-primary text-sm mx-1">{f.value}</p>
                <button
                  onClick={() => handleRemoveFilter(f.key, f.id)}
                  className="ml-2 text-primary hover:text-red-500"
                  aria-label={`Remove ${f.prefix} ${f.value}`}
                >
                  <i className="fas fa-times text-xs"></i>
                </button>
              </div>
            ))}
          </div>
        </div>
        <div className="ml-auto gap-2 shrink-0">
          <BaseTextField
            placeholder="Search"
            onChange={(e) => {
              setSearch(e.target.value);
              handleSearchChange(e.target.value);
            }}
            value={search}
            startAdornment={<i className="fa-light fa-magnifying-glass text-lg" />}
            className="w-full sm:w-[300px] lg:w-[350px]"
          />
        </div>
      </div>

      {/* Kanban board */}
      <DragDropContext onDragEnd={onDragEnd}>
        <div className="grid grid-cols-4 gap-4 mt-4">
          {COLUMNS.map(({ status, label, hasBorder }) => (
            <KanbanColumn
              key={status}
              status={status}
              label={label}
              hasBorder={hasBorder}
              query={columnQueries[status]}
              handleTaskClick={handleTaskClick}
              refetchAllTeamTask={refetchAllTeamTask}
            />
          ))}
        </div>
      </DragDropContext>

      {isFilterDrawerOpen && (
        <TaskBoardFilters
          open={isFilterDrawerOpen}
          onClose={() => setIsFilterDrawerOpen(false)}
          fetchTasks={refetchAllTeamTask}
          tasksList={tasksList}
          user={user}
          isLoading={false}
          isCompanyOwner={isCompanyOwner}
          teamsData={teamData}
          teamMembersData={teamMembersData}
          allTeamTaskData={allTeamTaskData}
          companyUsersData={companyUsers?.data?.users}
        />
      )}
    </BaseWrapper>
  );
};

// Per-status column with infinite scroll and droppable
const KanbanColumn = ({ status, label, hasBorder, query, handleTaskClick, refetchAllTeamTask }) => {
  const tasks = query.data?.pages.flatMap((p) => p.data?.data || []) || [];
  const totalCount = query.data?.pages[0]?.data?.totalCount ?? 0;
  const sentinelRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    const el = sentinelRef.current;
    const root = scrollRef.current;
    if (!el || !root) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && query.hasNextPage && !query.isFetching) {
          query.fetchNextPage();
        }
      },
      { threshold: 0.1, root },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [query.hasNextPage, query.isFetching]);

  return (
    <div>
      <h6 className="font-medium !text-zinc-400 text-center mb-2">
        {label} ({totalCount})
      </h6>
      <Droppable droppableId={status}>
        {(provided, snapshot) => (
          <div
            ref={(el) => {
              provided.innerRef(el);
              scrollRef.current = el;
            }}
            {...provided.droppableProps}
            className={`${hasBorder ? "border-r pr-3" : "pr-3"} overflow-y-auto h-[calc(100vh-300px)] transition-colors ${
              snapshot.isDraggingOver ? "bg-blue-50/40 rounded-lg" : ""
            }`}
          >
            {query.isPending
              ? Array(3)
                  .fill(0)
                  .map((_, i) => <TaskCardSkeleton key={i} />)
              : tasks.map((task, index) => (
                  <Draggable key={task.id} draggableId={task.id} index={index}>
                    {(dragProvided, dragSnapshot) => (
                      <div
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        {...dragProvided.dragHandleProps}
                        style={{
                          ...dragProvided.draggableProps.style,
                          opacity: dragSnapshot.isDragging ? 0.85 : 1,
                        }}
                      >
                        <TaskCard
                          key={task.id}
                          taskId={task.id}
                          referenceId={task.referenceId}
                          status={task.status}
                          title={task?.title}
                          description={task?.description}
                          dueDate={task?.dueDate}
                          priority={task?.priority}
                          assignees={task?.assignees}
                          workOrder={task?.workOrder}
                          totalComments={task?.totalComments}
                          onClick={() => handleTaskClick(task)}
                          refetchAllTeamTask={refetchAllTeamTask}
                          isDragging={dragSnapshot.isDragging}
                        />
                      </div>
                    )}
                  </Draggable>
                ))}
            {provided.placeholder}
            {/* Infinite scroll sentinel */}
            <div ref={sentinelRef} className="h-1" />
            {query.isFetchingNextPage && (
              <div className="flex justify-center py-3">
                <Oval
                  height="24"
                  width="24"
                  color={brandSecondaryColor}
                  secondaryColor="#ff9b60"
                  ariaLabel="loading-more"
                />
              </div>
            )}
          </div>
        )}
      </Droppable>
    </div>
  );
};

const TaskCardSkeleton = () => {
  return (
    <div className="bg-white shadow-md rounded-xl py-4 px-4 cursor-default mb-3 max-h-[295px] animate-pulse">
      <div className="flex justify-between items-center mb-4">
        <Skeleton variant="text" width={120} height={28} />
        <Skeleton variant="rectangular" width={80} height={32} />
      </div>
      <Skeleton variant="text" width="100%" height={40} className="mb-3" />
      <div className="flex gap-3 mb-4">
        <Skeleton variant="rectangular" width={80} height={24} />
        <Skeleton variant="rectangular" width={80} height={24} />
      </div>
      <div className="flex justify-between items-end border-t pt-3">
        <AvatarGroup spacing="small">
          <Skeleton variant="circular" width={36} height={36} />
          <Skeleton variant="circular" width={36} height={36} />
          <Skeleton variant="circular" width={36} height={36} />
        </AvatarGroup>
        <div className="flex items-center gap-4">
          <div className="flex flex-col items-center">
            <Skeleton variant="circular" width={24} height={24} />
            <Skeleton variant="text" width={50} height={12} />
          </div>
          <div className="flex flex-col items-center">
            <Skeleton variant="rectangular" width={24} height={24} className="mb-1" />
            <Skeleton variant="text" width={40} height={12} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskBoardListing;
